import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Button } from "reactstrap";
import { toast } from "react-toastify";
import { MONITORING_TABS } from "./monitoringMock";
import {
	pauseMonitoring,
	reauditMonitoringNow,
	resumeMonitoring,
	runMonitoringNow,
} from "../../../services/monitoringApi";

const MONITORING_BASE = "/aivisibility/monitoring";

export default function MonitoringShell({
	title,
	project,
	websiteId,
	activeSection,
	onProjectChange,
	navLocked = false,
	onNavBlocked,
	children,
}) {
	const status = String(project?.status || "active").toLowerCase();
	const isActive = status === "active";
	const isPaused = status === "paused";
	const [busy, setBusy] = useState(null);

	async function runAction(key, fn, successMessage) {
		if (!websiteId || busy) return;
		setBusy(key);
		try {
			const res = await fn(websiteId);
			if (!res.ok) {
				toast.error(res.message || "Action failed");
				return;
			}
			toast.success(successMessage);
			if (onProjectChange) await onProjectChange();
		} catch {
			toast.error("Action failed");
		} finally {
			setBusy(null);
		}
	}

	return (
		<div className="monitoring">
			<div className="monitoring__page-header">
				<div className="monitoring__page-header-copy">
					<h2 className="monitoring__page-title mb-0">{title}</h2>
					{(isActive || isPaused || project?.hostname) && (
						<div className="monitoring__page-header-meta">
							{isActive && (
								<span className="monitoring__active-badge">
									<span className="monitoring__active-dot" aria-hidden="true" />
									Active
								</span>
							)}
							{isPaused && (
								<span className="monitoring__paused-badge">Paused</span>
							)}
							{project?.hostname && (
								<span className="monitoring__project-host text-muted">
									{project.hostname}
								</span>
							)}
						</div>
					)}
				</div>
				<div className="monitoring__project-actions">
					<Button
						color="primary"
						size="sm"
						className="monitoring__action-btn monitoring__action-btn--primary"
						disabled={Boolean(busy)}
						onClick={() =>
							runAction("run", runMonitoringNow, "Prompt run queued")
						}
					>
						{busy === "run" ? "Queuing…" : "Run now"}
					</Button>
					<Button
						size="sm"
						className="monitoring__action-btn"
						disabled={Boolean(busy)}
						onClick={() => {
							if (
								!window.confirm(
									"Run a full re-audit now? It takes several minutes and produces a new report plus a new point on the History score chart."
								)
							) {
								return;
							}
							runAction(
								"reaudit",
								reauditMonitoringNow,
								"Re-audit started — the new report appears on History when it finishes."
							);
						}}
					>
						{busy === "reaudit" ? "Starting…" : "Re-audit now"}
					</Button>
					{isActive ? (
						<Button
							size="sm"
							className="monitoring__action-btn"
							disabled={Boolean(busy)}
							onClick={() =>
								runAction("pause", pauseMonitoring, "Project paused")
							}
						>
							{busy === "pause" ? "Pausing…" : "Pause project"}
						</Button>
					) : (
						<Button
							size="sm"
							className="monitoring__action-btn"
							disabled={Boolean(busy)}
							onClick={() =>
								runAction("resume", resumeMonitoring, "Project resumed")
							}
						>
							{busy === "resume" ? "Resuming…" : "Resume"}
						</Button>
					)}
				</div>
			</div>

			<nav
				className="monitoring__tabs"
				role="tablist"
				aria-label="Monitoring sections"
			>
				{MONITORING_TABS.filter((tab) => !tab.hidden).map((tab) => {
					if (tab.disabled) {
						return (
							<span
								key={tab.id}
								role="tab"
								aria-disabled="true"
								aria-selected={false}
								className="monitoring__tab monitoring__tab--disabled"
								title="Coming soon"
							>
								{tab.label}
							</span>
						);
					}
					const isCurrent = activeSection === tab.id;
					if (navLocked && !isCurrent) {
						return (
							<button
								key={tab.id}
								type="button"
								role="tab"
								aria-disabled="true"
								aria-selected={false}
								className="monitoring__tab monitoring__tab--nav-blocked"
								title="Finish or cancel the open prompt dialog before switching tabs"
								onClick={() => onNavBlocked?.()}
							>
								{tab.label}
							</button>
						);
					}
					return (
						<NavLink
							key={tab.id}
							to={`${MONITORING_BASE}/${tab.id}`}
							role="tab"
							aria-selected={isCurrent}
							className={({ isActive: linkActive }) =>
								`monitoring__tab${linkActive || isCurrent ? " monitoring__tab--active" : ""}`
							}
							end
						>
							{tab.label}
						</NavLink>
					);
				})}
			</nav>

			<div className="monitoring__tab-panel" role="region" aria-label="Monitoring content">
				{children}
			</div>
		</div>
	);
}
